import { createContext, useContext, useEffect, useState, useCallback, useRef, createElement, type ReactNode } from "react";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import { format, toZonedTime } from "date-fns-tz";
import { io } from "socket.io-client";
import { useAuthCache } from "@/shared/cache/slices/auth";
import { toast } from "sonner";
import type { Entry } from "@/shared/types/entries";
import { APP_ICON_MAP } from "@/v2/lib/appIcons";

type DataContextType = {
	entries: Entry[];
	exportedEntries: Entry[];
	isLoading: boolean;
	refreshRecords: () => Promise<void>;
	updateEntries: (updater: (prev: Entry[]) => Entry[]) => () => void;
};

const DataContext = createContext<DataContextType | undefined>(undefined);

function transformData(data: any[]): Entry[] {
	const formatInTimeZone = (parsedTime: Date, fmt: string, tz: string) =>
		format(toZonedTime(parsedTime, tz), fmt, { timeZone: tz });

	return (data || [])
		.map((record: any) => ({
			id: Number(record.id),
			date: record.date
				? formatInTimeZone(new Date(record.date), "MM/dd/yyyy", "UTC")
				: "",
			matter: record.matter || record.matter_name || "",
			matter_id: record.matter_id || "",
			matter_name: record.matter_name || "",
			client: record.client || "",
			description: record.description || "",
			task: record.task || "",
			activity: record.activity || "",
			document_name: record.document_name || "",
			duration: parseFloat(record.duration || record.total_hours) || 0,
			units_seconds: record.units_seconds || 0,
			start_time: record.start_time || "",
			end_time: record.end_time || "",
			application: record.application || "",
			// Coerce to clean boolean | null. MSSQL driver may return BIT columns as
			// numeric 1/0 (the admin endpoint wraps with Boolean() for the same reason).
			billable: record.billable == null ? null : Boolean(record.billable),
			approved: record.approved,
			needs_improvement: record.needs_improvement,
			attorney_feedback: record.attorney_feedback,
		}))
		.sort((a, b) => {
			if (a.end_time && b.end_time) {
				return new Date(b.end_time).getTime() - new Date(a.end_time).getTime();
			}
			return 0;
		});
}

function getApiBaseUrl(): string {
	const hostname = window.location.hostname;
	if (hostname.includes("localhost")) return "http://localhost:8000";
	if (hostname.includes("bpmlaw")) return "https://hourglassapi.bpmlaw.com";
	if (hostname.includes("tysonmendes")) return "https://hourglassapi.tysonmendes.com";
	if (hostname.includes("pregodonnell")) return "https://hourglassapi.pregodonnell.com";
	if (hostname.includes("app.hourglass.law")) return "https://api.hourglass.law";
	const subdomain = hostname.split(".")[0];
	return `https://${subdomain}api.hourglass.law`;
}

export function DataProvider({ children }: { children: ReactNode }) {
	const { makeApiRequest } = useApi();
	const { auth, isAuthLoading } = useAuthCache();

	const [entries, setEntries] = useState<Entry[]>([]);
	const [exportedEntries, setExportedEntries] = useState<Entry[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	const emailRef = useRef(auth.email);
	useEffect(() => { emailRef.current = auth.email; }, [auth.email]);
	const refreshRecordsRef = useRef<(() => Promise<void>) | null>(null);

	const updateEntries = useCallback((updater: (prev: Entry[]) => Entry[]) => {
		let snapshot: Entry[];
		setEntries(prev => { snapshot = prev; return updater(prev); });
		return () => setEntries(snapshot!);
	}, []);

	const refreshRecords = useCallback(async () => {
		try {
			const [recordsResponse, exportedResponse] = await Promise.all([
				makeApiRequest({ endpoint: "/records", method: "GET" }),
				makeApiRequest({ endpoint: "/records/exported", method: "GET" }),
			]);
			setEntries(transformData(recordsResponse.data || []));
			setExportedEntries(transformData(exportedResponse.data || []));
		} catch (e) {
			if (e instanceof Error) {
				logger.captureException(e, { action: "refreshRecords" });
			}
			toast.error("Failed to load entries");
		}
	}, [makeApiRequest]);
	useEffect(() => { refreshRecordsRef.current = refreshRecords; }, [refreshRecords]);

	// Initial load
	useEffect(() => {
		if (!isAuthLoading && auth.email) {
			setIsLoading(true);
			refreshRecords().finally(() => setIsLoading(false));
		}
	}, [isAuthLoading, auth.email]); // eslint-disable-line react-hooks/exhaustive-deps

	// WebSocket for real-time updates
	useEffect(() => {
		const socket = io(getApiBaseUrl(), { transports: ["websocket"] });

		socket.on("entry processing started", (data: { email: string; application?: string }) => {
			if (data.email === emailRef.current) {
				const appIconSrc = data.application ? APP_ICON_MAP[data.application] : null;
				toast.loading("Processing entry...", {
					id: "entry-processing",
					duration: Infinity,
					icon: appIconSrc ? createElement("img", { src: appIconSrc, alt: data.application, className: "size-5" }) : undefined,
					description: data.application || undefined,
				});
			}
		});

		socket.on("entry processing completed", (data) => {
			if (data.email === emailRef.current) {
				toast.dismiss("entry-processing");
				refreshRecordsRef.current?.();
			}
		});

		socket.on("entry_feedback_updated", (data) => {
			if (data.email === emailRef.current) refreshRecordsRef.current?.();
		});

		return () => { socket.disconnect(); };
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<DataContext.Provider value={{ entries, exportedEntries, isLoading, refreshRecords, updateEntries }}>
			{children}
		</DataContext.Provider>
	);
}

export function useData() {
	const context = useContext(DataContext);
	if (!context) throw new Error("useData must be used within DataProvider");
	return context;
}
